/**
 * CLE-2820 — the "AI Recommendation" label on the benefit tabs.
 *
 * The eligibility API returns `aiRecommendations`: per benefit, the AI's pick and a `pointer` to the
 * ONE row of `details` to label. This turns those pointers into a lookup the tabs can ask "is this
 * row an AI recommendation?". Display only — nothing prices from it, and it is shown to SuperAdmins
 * only. No field, or no pointer, means no label.
 *
 * 🚨 ONE LABEL PER PICK, FROM `pointer` ALONE. A plan-level co-insurance line is copied into every
 * care-setting tab's rows, so its `pointers` list names five rows for one pick — labelling from that
 * list put "AI Recommendation" on five tabs for a single answer. The API picks the row on the
 * account's own care setting; `pointers` stays in the payload for debugging and is NEVER read here.
 */

export interface IAiDisplayPointer {
  tab: string;
  row?: "summary" | "copay" | "coinsurance";
  field?: string;
  index?: number;
}

export interface IAiRecommendation {
  value: number | null;
  confidence: number | null;
  pointer: IAiDisplayPointer | null;
  /** Every row the pick maps to. 🚨 Debugging only — never label from this; see the note above. */
  pointers?: IAiDisplayPointer[];
}

export type IAiRecommendations = Record<string, IAiRecommendation | undefined>;

export const summaryKey = (tab: string, field: string) =>
  `${tab}|summary|${field}`;
export const lineKey = (
  tab: string,
  row: "copay" | "coinsurance",
  index: number
) => `${tab}|${row}|${index}`;
export const optionKey = (tab: string, index: number) => `${tab}|${index}`;

export const pointerKey = (pointer: IAiDisplayPointer): string | null => {
  if (!pointer || typeof pointer.tab !== "string") return null;
  if (pointer.row === "summary" && typeof pointer.field === "string") {
    return summaryKey(pointer.tab, pointer.field);
  }
  if (
    (pointer.row === "copay" || pointer.row === "coinsurance") &&
    Number.isInteger(pointer.index)
  ) {
    return lineKey(pointer.tab, pointer.row, pointer.index as number);
  }
  if (pointer.row === undefined && Number.isInteger(pointer.index)) {
    return optionKey(pointer.tab, pointer.index as number);
  }
  return null;
};

/** The one row key per benefit, from `pointer`. Tolerates a missing or malformed field. */
export const buildAiTagIndex = (
  aiRecommendations: IAiRecommendations | null | undefined
): Set<string> => {
  const keys = new Set<string>();
  if (!aiRecommendations || typeof aiRecommendations !== "object") return keys;
  Object.values(aiRecommendations).forEach((recommendation) => {
    const pointer = recommendation?.pointer;
    const key = pointer ? pointerKey(pointer) : null;
    if (key) keys.add(key);
  });
  return keys;
};

/** The summary field each tab item renders, by the item's subtitle. */
const FIELD_BY_SUBTITLE: Record<string, string> = {
  Copay: "CoPayInNet",
  Coinsurance: "CoInsInNet",
  "Remaining Deductible": "IndividualDeductibleRemainingInNet",
};

const indexesFor = (
  tags: Set<string>,
  tab: string,
  row: "copay" | "coinsurance",
  rows: unknown
): number[] =>
  Array.isArray(rows)
    ? rows
        .map((_, index) => index)
        .filter((index) => tags.has(lineKey(tab, row, index)))
    : [];

/**
 * Marks a care-setting tab's items: the summary value row, and which of its additional copay /
 * co-insurance rows (by index into `details[tab].serviceDetails`) are AI recommendations.
 */
export const tagTabItems = <T extends Record<string, any>>(
  items: T[],
  tab: string | undefined,
  tags: Set<string>
): T[] => {
  if (!tab || tags.size === 0) return items;
  return items.map((item) => {
    const field = FIELD_BY_SUBTITLE[item.subtitle];
    return {
      ...item,
      aiRecommended:
        Boolean(field) &&
        item.Value !== null &&
        item.Value !== undefined &&
        tags.has(summaryKey(tab, field)),
      aiCopayIndexes: indexesFor(tags, tab, "copay", item.additionalCopay),
      aiCoinsuranceIndexes: indexesFor(
        tags,
        tab,
        "coinsurance",
        item.additionalCoinsurance
      ),
    };
  });
};

/** Marks plan-level summary rows (deductible / out-of-pocket) that come from one summary field. */
export const tagSummaryItems = <T extends Record<string, any>>(
  items: T[],
  field: string,
  tags: Set<string>
): T[] => {
  if (tags.size === 0) return items;
  const tagged = tags.has(summaryKey("HBPC_Deductible_OOP_Summary", field));
  return tagged
    ? items.map((item) => ({ ...item, aiRecommended: item.Value !== "N/A" }))
    : items;
};

/** Marks option rows by their index in the unsplit `details[tab]` array (`originalIndex`). */
export const tagOptionItems = <T extends Record<string, any>>(
  items: T[],
  tab: "otherDeductibleOptions" | "otherRemainingOutOfPocketMaxOptions",
  tags: Set<string>
): T[] => {
  if (tags.size === 0) return items;
  return items.map((item) => ({
    ...item,
    aiRecommended:
      Number.isInteger(item.originalIndex) &&
      tags.has(optionKey(tab, item.originalIndex)),
  }));
};
